import Pusher from 'pusher-js'
import { parseISO, add, isAfter } from 'date-fns'

const KEYS = ['sentAt', 'updatedAt']

export default function createPusherManager(
  handlePayload,
  handleLatePayload,
  handleConnected,
  handleSubscribed
) {
  let storedChannelName
  let lastPayloadTimes = {}

  let config = {
    cluster: window.ENV.PUSHER_REGION,
    forceTLS: true,
    authEndpoint: '/api/pusher/auth',
  }
  if (window.ENV.PUSHER_HOST) {
    config = {
      ...config,
      wsHost: window.ENV.PUSHER_HOST,
      httpHost: window.ENV.PUSHER_HOST,
    }
  }

  const pusher = new Pusher(window.ENV.PUSHER_KEY, config)

  pusher.connection.bind('state_change', ({ current }) => {
    if (current === 'connected') {
      console.log('Pusher connected')
      if (handleConnected) {
        handleConnected()
      }
    } else {
      console.log('Pusher disconnected')
    }
  })

  // True if this message carries any timestamp older than what we've already
  // applied — applying it would clobber newer state with stale data.
  // sentAt: the time this payload was dispatched from the server.
  // updatedAt: the internal time this payload refers to in state.
  function isOlderThanSeen(message) {
    return KEYS.some((key) => {
      if (typeof message[key] === 'undefined') return false
      const last = lastPayloadTimes[key]
      return last !== undefined && isAfter(last, parseISO(message[key]))
    })
  }

  function recordTimestamps(message) {
    KEYS.forEach((key) => {
      if (typeof message[key] !== 'undefined') {
        lastPayloadTimes[key] = parseISO(message[key])
      }
    })
  }

  function subscribeToChannel(channelName) {
    const channel = pusher.subscribe(channelName)

    // Fires once the channel subscription is actually established (and again
    // after any reconnect). Use this to re-pull current state, so a payload
    // that was broadcast during the subscription handshake — and therefore
    // never delivered to us — is caught up rather than lost forever.
    channel.bind('pusher:subscription_succeeded', () => {
      if (handleSubscribed) handleSubscribed(storedChannelName)
    })

    channel.bind('changed', (snakeMessage) => {
      const message = camelizeKeys(snakeMessage)

      // Out of order: a newer broadcast already landed (or our GET-driven
      // snapshot already moved the state forward). Trigger a recovery
      // re-fetch as a safety net and drop this stale message rather than
      // recording its older timestamp or clobbering newer state.
      if (isOlderThanSeen(message)) {
        handleLatePayload(storedChannelName, 'out_of_order')
        if (!window.ENV.CIRCLE_CI) {
          throw new OutOfOrderPayloadError("Payload arrived out of order", message);
        }
        return
      }

      // Late: broadcast was delayed in transit (>5s) but its content is the
      // latest we have. Trigger a re-fetch as a safety net; apply normally.
      if (
        typeof message.sentAt !== 'undefined' &&
        isAfter(new Date(), add(parseISO(message.sentAt), { seconds: 5 }))
      ) {
        handleLatePayload(storedChannelName, 'late')
        if (!window.ENV.CIRCLE_CI) {
          throw new LatePayloadError("Payload arrived late", message);
        }
      }

      recordTimestamps(message)
      handlePayload(message)
    })
  }

  // Apply a payload fetched via GET (after pusher:subscription_succeeded, or
  // handleLatePayload's recovery re-fetch, or any other catch-up). The GET
  // response may have been in flight while a 'changed' for newer state landed
  // over the socket; if so, skip — our local state is already fresher.
  function applyServerSnapshot(message) {
    if (isOlderThanSeen(message)) return
    recordTimestamps(message)
    handlePayload(message)
  }

  function unsubscribeFromChannel(channelName) {
    pusher.unsubscribe(channelName)
  }

  function setChannelName(channelName) {
    storedChannelName = channelName
    const fullChannelName = `private-${window.ENV.PUSHER_CHANNEL_PREFIX}-${channelName}`

    subscribeToChannel(fullChannelName)
    return () => unsubscribeFromChannel(fullChannelName)
  }

  return {
    setChannelName,
    applyServerSnapshot,
  }
}
class LatePayloadError extends Error {
  constructor(message, payload) {
    super(`Payload arrived more than 5 seconds late`);
    this.name = "LatePayloadError"; // Custom name for the error
    this.debugMessage = `Late payload details: ${JSON.stringify(payload)}`; // Detailed info for debugging
    this.payload = payload;
  }
}

class OutOfOrderPayloadError extends Error {
  constructor(message, payload) {
    super(`Payload arrived out of order`);
    this.name = "OutOfOrderPayloadError"; // Custom name for the error
    this.debugMessage = `Out of order payload details: ${JSON.stringify(payload)}`; // Detailed info for debugging
    this.payload = payload;
  }
}
